const fetchAs = require("./fetchAs");
const fetchList = require("./fetchList");
const fetchListContentFilters = require("./fetchListContentFilters");
const fetchListTaxonomyFilters = require("./fetchListTaxonomyFilters");
const fetchNavItem = require("./fetchNavItem");
const transformListData = require("./transformListData");
const transformText = require("./transformText");

/**
 * Fetch an entity and related entities up to 4 levels deep
 *
 * @param endpoint: '/type/bundle/uuid'
 * @param bundles: an array of names of all drupal bundle names
 * @param nodesConfig: nodes configuration object coming from plugin options
 * @param dispatch: a function to make authenticated requests (optional)
 * @param headers: headers to use in anonymous fetch (optional)
 * @param agent: agent to use in anonymous fetch (optional)
 */

module.exports = async function fetchEntity({
	endpoint,
	bundles = null,
	dispatch = null,
	headers = null,
	agent = null,
	maxDepth = 6,
	ssr = false,
	configs = null,
}) {
	let include_rels = ["thumbnail"];
	let prevent_rels = [
		"field_parent_entity",
		"field_parent",
		"field_entities_list_by_types",
		"node_type",
		"revision_uid",
		"uid",
		// "field_entities_list_by_mtypes",
	];
	let force_relationships_fetch = ["node--resources", "node--gallery"];
	let restrict_relationships_fields = [
		"field_entities_list_by_content",
		"field_entities_list_by_specific",
	];
	let bundle_in = ["image"]; // include these bundle relationships into recursive fetching
	let bundle_rels = bundles
		? bundles.filter((b) => !bundle_in.includes(b)).map((b) => `field_${b}`)
		: [];

	// related entities included in relationships will be packed in this object
	let includes = {};

	/**
	 * Recursive function to fetch entities as anonymous or authenticated user
	 * @param _endpoint: '/type/bundle/uuid'
	 * @param _depth: current cycle depth, starts at 0
	 * @param justAttributes: if true, prevents digging into relationships
	 * @returns entity object, entity.attributes, entity.relationships and includes object with related entities
	 */
	async function _fetch(
		_endpoint,
		_depth,
		justAttributes = false,
		mainRelKey = null
	) {
		let _entity = {}; // the entity to return
		let _includes = {}; // linear object packing related entities
		let relReferences = [];
		let response = await fetchAs({
			endpoint: _endpoint,
			dispatch,
			headers,
			agent,
			ssr,
		});
		let is_main_entity = false;
		if (response && response.data) {
			_entity = response.data;
			let config = configs[_entity.type];
			is_main_entity =
				config &&
				config.slugTitleField &&
				_entity &&
				_entity.attributes &&
				_entity.attributes.drupal_internal__nid;
			if (
				!_depth &&
				response.data.attributes &&
				response.data.attributes.body &&
				response.data.attributes.body.value
			) {
				response.data.attributes.body.transformed = await transformText(
					response.data.attributes.body.value,
					dispatch
				);
			}

			/**
			 * decide wether to dig into relationships or to stop here
			 */
			if (!justAttributes && response.data.relationships) {
				if (_depth < maxDepth) {
					let force_rels_fetch = force_relationships_fetch.includes(
						_entity.type
					);

					for (let rel_arr of Object.entries(response.data.relationships)) {
						const rel_key = rel_arr[0];
						const rel_data = rel_arr[1];
						/**
						 * fetch only entities from relevant relationships
						 */
						if (
							!prevent_rels.includes(rel_key) &&
							(rel_key.includes("field_") || include_rels.includes(rel_key))
						) {
							if (rel_data && rel_data.data) {
								let rels = Array.isArray(rel_data.data)
									? rel_data.data
									: [rel_data.data];
								relReferences = relReferences.concat(
									rels
										.filter(
											(r) =>
												typeof includes[r.id] === "undefined" &&
												r.type !== "unknown" &&
												r.id !== "missing"
										)
										.map((rel) => {
											let rel_type = rel.type.split("--");
											let rel_endpoint = `${rel_type[0]}/${rel_type[1]}/${rel.id}`;
											/**
											 * if relationship name is like field_bundle_name or if it's a taxonomy relationship
											 * then _fetch justAttributes to prevent fetching unnecesary data
											 */
											let is_bundle_rel = bundle_rels.includes(rel_key);
											let is_taxonomy_rel = rel_type[0] === "taxonomy_term";
											let is_media = rel_key === "field_media";
											let fetch_just_attrs =
												(restrict_relationships_fields.includes(rel_key) ||
													is_bundle_rel ||
													is_taxonomy_rel ||
													is_media ||
													rel.type === "node-entities_list") &&
												!force_rels_fetch;
											return {
												endpoint: rel_endpoint,
												depth: _depth + 1,
												justAttrs: fetch_just_attrs,
												mainKey: !_depth ? rel_key : mainRelKey,
											};
										})
								);
							}
						}
					}
				}
			}

			/**
			 * Insert slug into entity
			 */
			if (is_main_entity) {
				let nav_item = await fetchNavItem(_entity);
				if (nav_item) {
					_entity.attributes.slug = nav_item.slug;
				}
			}
		} else {
			console.error(
				`fetching ${!_depth ? "main" : "dependency"} entity failed`,
				_endpoint
			);
		}

		const relReponses = await Promise.all(
			relReferences.map((ref) =>
				_fetch(ref.endpoint, ref.depth, ref.justAttrs, ref.mainKey)
			)
		);
		if (Array.isArray(relReponses) && relReponses.length > 0) {
			for (const rel_entity of relReponses) {
				if (rel_entity.type === "node--text") {
					if (
						!mainRelKey ||
						[
							"field_section_main",
							"field_section_closing",
							"field_section_sidebar",
						].includes(mainRelKey)
					) {
						if (
							rel_entity.attributes &&
							rel_entity.attributes.body &&
							rel_entity.attributes.body.value
						) {
							rel_entity.attributes.body.transformed = await transformText(
								rel_entity.attributes.body.value,
								dispatch
							);
						}
					}
				}
				_includes[rel_entity.id] = rel_entity;
			}
		}
		includes = { ...includes, ..._includes };
		if (
			typeof _entity.relationships !== "undefined" &&
			Object.keys(_entity.relationships).length > 0
		) {
			if (_entity.relationships.uid && _entity.relationships.uid.data) {
				_entity.author = _entity.relationships.uid.data.id;
			}
			delete _entity.relationships.node_type;
			delete _entity.relationships.revision_uid;
			delete _entity.relationships.revision_user;
			delete _entity.relationships.uid;
			delete _entity.relationships.parent;
			for (let rel_arr of Object.entries(_entity.relationships)) {
				const rel_field_name = rel_arr[0];
				const rel_data = rel_arr[1];
				delete _entity.relationships[rel_field_name].links;
			}
		}
		if (
			typeof _entity.attributes !== "undefined" &&
			Object.keys(_entity.attributes).length > 0
		) {
			delete _entity.attributes.langcode;
			delete _entity.attributes.revision_timestamp;
			delete _entity.attributes.revision_created;
			delete _entity.attributes.weight;
			delete _entity.attributes.revision_log;
			delete _entity.attributes.revision_log_message;
			delete _entity.attributes.status;
			delete _entity.attributes.default_langcode;
			delete _entity.attributes.revision_translation_affected;
		}
		delete _entity.links;
		return _entity;
	}

	/**
	 * kick off recursive calling
	 */
	const entity = await _fetch(endpoint, 0);

	const entity_lists = Object.values(includes)
		.filter((i) => i.type === "node--entities_list")
		.map((entities_list_entity) => transformListData(entities_list_entity));

	let lists = {};
	for (const list_data of entity_lists) {
		lists[list_data.id] = {
			...list_data,
			items: null,
			page: null,
		};
		if (!list_data.showFilters && !list_data.useElastic) {
			let _excerpts = await fetchList({
				list_data,
				configs,
				includes,
				dispatch: null,
				relationships: entity.relationships,
			});
			lists[list_data.id].items = _excerpts.items;
			lists[list_data.id].page = _excerpts.page;
		} else {
			lists[list_data.id].filteringByContent = await fetchListContentFilters(
				list_data,
				dispatch
			);
			lists[list_data.id].filteringByTerms = await fetchListTaxonomyFilters(
				list_data,
				dispatch
			);
			console.warn("list will be fetched on client");
		}
	}
	return { entity, includes, lists };
};
